import {
    Card,
    CardContent,
    CardHeader,
    Divider,
    FormControl,
    FormControlLabel,
    Grid,
    Switch,
    TextField,
    Typography,
    Box,
    Alert,
    IconButton,
    InputAdornment
} from "@mui/material";
import { Visibility, VisibilityOff, VpnKey } from "@mui/icons-material";
import { useState } from "react";

export default function PortalAccessCard({ formik }) {
    const [showPassword, setShowPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);
    const [newPassword, setNewPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [passwordError, setPasswordError] = useState('');

    const handlePortalAccessChange = (event) => {
        const isEnabled = event.target.checked;
        formik.setFieldValue('portalAccess', isEnabled);

        // If enabling portal access but no password exists, require setting one
        if (isEnabled && !formik.values.portalPassword) {
            // User must set password
        } else if (!isEnabled) {
            // Clear password fields when disabling
            setNewPassword('');
            setConfirmPassword('');
            setPasswordError('');
        }
    };

    const handlePasswordChange = (field, value) => {
        if (field === 'new') {
            setNewPassword(value);
            // Validate password
            if (value.length > 0 && value.length < 8) {
                setPasswordError('Password must be at least 8 characters');
            } else if (confirmPassword && value !== confirmPassword) {
                setPasswordError('Passwords do not match');
            } else {
                setPasswordError('');
                if (value.length >= 8 && value === confirmPassword) {
                    // Valid password, update formik
                    formik.setFieldValue('newPortalPassword', value);
                }
            }
        } else {
            setConfirmPassword(value);
            // Check if passwords match
            if (newPassword && value !== newPassword) {
                setPasswordError('Passwords do not match');
            } else {
                setPasswordError('');
                if (newPassword.length >= 8 && value === newPassword) {
                    // Valid password, update formik
                    formik.setFieldValue('newPortalPassword', newPassword);
                }
            }
        }
    };

    const hasExistingPassword = formik.values.portalPassword;
    const needsPassword = formik.values.portalAccess && !hasExistingPassword && !newPassword;

    return (
        <Card>
            <CardHeader
                title={
                    <Box display="flex" alignItems="center" gap={1}>
                        <VpnKey fontSize="small" />
                        <Typography variant="h6">Portal Access</Typography>
                    </Box>
                }
                subheader="Allow customer to access the patient portal"
            />
            <Divider />
            <CardContent>
                <Grid container spacing={2}>
                    {/* Enable/Disable Portal Access */}
                    <Grid item xs={12}>
                        <FormControl fullWidth>
                            <FormControlLabel
                                control={
                                    <Switch
                                        checked={formik.values.portalAccess || false}
                                        onChange={handlePortalAccessChange}
                                        color="primary"
                                    />
                                }
                                label={
                                    <Box>
                                        <Typography variant="body1" fontWeight={500}>
                                            Enable Portal Access
                                        </Typography>
                                        <Typography variant="caption" color="text.secondary">
                                            Customer can view appointments, invoices, and prescriptions online
                                        </Typography>
                                    </Box>
                                }
                            />
                        </FormControl>
                    </Grid>

                    {/* Status Message */}
                    {formik.values.portalAccess && (
                        <Grid item xs={12}>
                            {hasExistingPassword ? (
                                <Alert severity="success" variant="outlined">
                                    Portal access is enabled. Customer can login with their existing password.
                                </Alert>
                            ) : (
                                <Alert severity="warning" variant="outlined">
                                    Please set an initial password for the customer to login.
                                </Alert>
                            )}
                        </Grid>
                    )}

                    {/* Password Fields (only show if portal is enabled) */}
                    {formik.values.portalAccess && (
                        <>
                            <Grid item xs={12}>
                                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                                    {hasExistingPassword ? 'Change Password (optional)' : 'Set Initial Password *'}
                                </Typography>
                            </Grid>

                            <Grid item xs={12} sm={6}>
                                <TextField
                                    fullWidth
                                    label="New Password"
                                    type={showPassword ? 'text' : 'password'}
                                    value={newPassword}
                                    onChange={(e) => handlePasswordChange('new', e.target.value)}
                                    error={!!passwordError && newPassword.length > 0}
                                    helperText={newPassword.length > 0 && newPassword.length < 8 ? 'At least 8 characters' : ''}
                                    required={!hasExistingPassword}
                                    InputProps={{
                                        endAdornment: (
                                            <InputAdornment position="end">
                                                <IconButton
                                                    onClick={() => setShowPassword(!showPassword)}
                                                    edge="end"
                                                    size="small"
                                                >
                                                    {showPassword ? <VisibilityOff /> : <Visibility />}
                                                </IconButton>
                                            </InputAdornment>
                                        )
                                    }}
                                />
                            </Grid>

                            <Grid item xs={12} sm={6}>
                                <TextField
                                    fullWidth
                                    label="Confirm Password"
                                    type={showConfirmPassword ? 'text' : 'password'}
                                    value={confirmPassword}
                                    onChange={(e) => handlePasswordChange('confirm', e.target.value)}
                                    error={!!passwordError && confirmPassword.length > 0}
                                    helperText={passwordError}
                                    required={!hasExistingPassword}
                                    InputProps={{
                                        endAdornment: (
                                            <InputAdornment position="end">
                                                <IconButton
                                                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                                                    edge="end"
                                                    size="small"
                                                >
                                                    {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                                                </IconButton>
                                            </InputAdornment>
                                        )
                                    }}
                                />
                            </Grid>

                            {/* Portal URL Info */}
                            {hasExistingPassword && (
                                <Grid item xs={12}>
                                    <Alert severity="info" variant="outlined">
                                        <Typography variant="body2">
                                            <strong>Portal URL:</strong> The customer can login at {window.location.origin}/login
                                        </Typography>
                                        <Typography variant="caption" display="block" mt={1}>
                                            Login credentials: Their email address and the password you set
                                        </Typography>
                                    </Alert>
                                </Grid>
                            )}
                        </>
                    )}

                    {/* Warning when disabling */}
                    {!formik.values.portalAccess && hasExistingPassword && (
                        <Grid item xs={12}>
                            <Alert severity="warning" variant="outlined">
                                Disabling portal access will prevent the customer from logging in. Their password will be preserved.
                            </Alert>
                        </Grid>
                    )}
                </Grid>
            </CardContent>
        </Card>
    );
}
